import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Logo } from "./Logo";

/* Official Discord logo (simple-icons 24x24 path) in currentColor so it
 * inherits the footer link's tint. */
const FooterDiscordIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </svg>
);

export const Footer = () => {
  const navigate = useNavigate();
  return (
    <footer className="relative">
      {/* Final CTA band */}
      <div className="mx-auto max-w-7xl px-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-[2rem] border border-[#E52525]/20 bg-[#141414] px-8 py-16 text-center sm:px-16 sm:py-20"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(229,37,37,0.22),transparent_60%)] blur-3xl"
          />
          <h2 className="font-heading relative text-4xl font-black tracking-tighter text-white sm:text-5xl">
            Ready to enter the pit?
          </h2>
          <p className="relative mx-auto mt-4 max-w-md text-lg font-light text-zinc-400">
            Your next practice partner is one click away.
          </p>
          <button
            data-testid="footer-cta-btn"
            onClick={() => navigate("/login")}
            className="group relative mt-8 inline-flex items-center justify-center gap-3 rounded-full bg-[#E52525] px-9 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(229,37,37,0.35)] transition-[background-color,transform] duration-300 hover:bg-[#FF3333] active:scale-95"
          >
            Enter The Pit
            <ArrowRight
              size={19}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </button>
        </motion.div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 py-10 sm:flex-row">
          <Logo className="text-xl" />
          <nav
            data-testid="footer-nav"
            className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-zinc-400"
          >
            <Link
              to="/about"
              data-testid="footer-link-about"
              className="transition-colors hover:text-white"
            >
              About
            </Link>
            <a
              href="https://discord.gg/ZxUnBHh8d"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Join the SalesPit Discord"
              data-testid="footer-link-discord"
              className="inline-flex items-center gap-1.5 text-zinc-300 transition-colors hover:text-white"
            >
              <FooterDiscordIcon size={14} />
              Join our Discord
            </a>
            <Link
              to="/privacy"
              data-testid="footer-link-privacy"
              className="transition-colors hover:text-white"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              data-testid="footer-link-terms"
              className="transition-colors hover:text-white"
            >
              Terms of Service
            </Link>
          </nav>
          <p className="text-sm text-zinc-600">
            © {new Date().getFullYear()} SalesPit
          </p>
        </div>
      </div>
    </footer>
  );
};
